import { useEffect, useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { DEFAULT_CANDIDATE_ACTIVE_YEARS } from '@dosfilos/domain';
import { tenantConfigApi } from '../../../lib/api/tenant-config.api';
import { errorText } from '../../../lib/error-text';
import { useTenantConfig } from '../../../hooks/use-tenant-config';

// ─── Candidatos a graduación: cuándo un alumno cuenta como activo (M2·H11) ──
//
// Activo = cursa ahora, o su último período con nota empezó hace N años o
// menos. Lo guarda `updateTenantConfig` (administración del seminario o
// plataforma), que lo valida y lo audita. Es PRESENTACIÓN: decide en qué
// pestaña de Reportes → Candidatos aparece cada alumno; no cambia su avance ni
// su expediente. Solo se muestra en los seminarios con expediente por
// credenciales, que son los que tienen Candidatos.

export function CandidateActivityEditor({ tenantId }: { tenantId: string }): JSX.Element | null {
  const { t } = useTranslation('sis');
  const queryClient = useQueryClient();
  const tenantConfig = useTenantConfig();
  const saved = tenantConfig.candidateActiveYears;
  const [draft, setDraft] = useState<number | ''>(saved);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  useEffect(() => setDraft(saved), [saved]);

  if (tenantConfig.academicRecord.mode !== 'credentials') return null;
  const valid = typeof draft === 'number' && Number.isInteger(draft) && draft >= 1 && draft <= 50;

  const save = async () => {
    if (!valid) return;
    setBusy(true);
    setMsg(null);
    try {
      await tenantConfigApi.updateTenantConfig({ tenantId, candidateActiveYears: draft });
      await queryClient.invalidateQueries({ queryKey: ['tenant-config-view', tenantId] });
      await queryClient.invalidateQueries({ queryKey: ['graduation-candidates', tenantId] });
      setMsg({ ok: true, text: t('settings.candidateActivity.saved') });
    } catch (e) {
      setMsg({ ok: false, text: errorText(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={700}>
        {t('settings.candidateActivity.title')}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
        {t('settings.candidateActivity.desc', { default: DEFAULT_CANDIDATE_ACTIVE_YEARS })}
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
        <TextField
          size="small"
          type="number"
          label={t('settings.candidateActivity.years')}
          value={draft}
          onChange={(e) => setDraft(e.target.value === '' ? '' : Number(e.target.value))}
          inputProps={{ min: 1, max: 50 }}
          error={!valid}
          helperText={
            valid
              ? t('settings.candidateActivity.helper', { count: Number(draft) })
              : t('settings.candidateActivity.invalid')
          }
          sx={{ maxWidth: 260 }}
        />
        <Button
          variant="outlined"
          onClick={() => void save()}
          disabled={busy || !valid || draft === saved}
        >
          {t('settings.candidateActivity.save')}
        </Button>
      </Stack>
      {msg && (
        <Alert severity={msg.ok ? 'success' : 'error'} sx={{ mt: 1.5 }}>
          {msg.text}
        </Alert>
      )}
    </Box>
  );
}
